import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BASE_URL, USER_URL } from "../../constant/api";

function AdminEditBrand() {
 const { brandId } = useParams();
   const navigate = useNavigate();
 
   const [brand, setBrand] = useState({
     brandName: "",
     logoFile: null,
     advertisementFile: null,
     logo: "",
     brand_banner: "",
   });
 
   const [isLoading, setIsLoading] = useState(true);
   const [isSubmitting, setIsSubmitting] = useState(false);
 
   // Fetch brand details when component mounts
   useEffect(() => {
     const fetchBrand = async () => {
       try {
         const response = await USER_URL.get(`/products/admin/brands/${brandId}/`);
         setBrand({
           brandName: response.data.Data.brandname,
           logo: response.data.Data.image,
           brand_banner: response.data.Data.brand_banner,
         });
       } catch (error) {
         console.error("Error fetching brand details:", error);
         toast.error("Failed to fetch brand details.");
       } finally {
         setIsLoading(false);
       }
     };
 
     fetchBrand();
   }, [brandId]);
 
   // Handle text input change
   const handleInputChange = (e) => {
     const { name, value } = e.target;
     setBrand((prev) => ({ ...prev, [name]: value }));
   };
 
   // Handle file uploads (Logo and Banner)
   const handleFileChange = (e, field) => {
     const file = e.target.files[0];
     if (file) {
       const newImageUrl = URL.createObjectURL(file); // Create a temporary URL for the file
       setBrand((prev) => ({
         ...prev,
         [`${field}File`]: file,
         [field === "logo" ? "logo" : "brand_banner"]: newImageUrl, // Update preview instantly
       }));
     }
   };
 
   // Cleanup object URLs when component unmounts
   useEffect(() => {
     return () => {
       if (brand.logo && brand.logo.startsWith("blob:")) URL.revokeObjectURL(brand.logo);
       if (brand.brand_banner && brand.brand_banner.startsWith("blob:")) URL.revokeObjectURL(brand.brand_banner);
     };
   }, [brand.logo, brand.brand_banner]);
 
   // Handle form submission
   const handleSubmit = async (e) => {
     e.preventDefault();
 
     const formData = new FormData();
     formData.append("brandname", brand.brandName);
 
     // Append the logo file if a new one is uploaded
     if (brand.logoFile) {
       formData.append("image", brand.logoFile);
     }
 
     // Append the banner file if a new one is uploaded
     if (brand.advertisementFile) {
       formData.append("brand_banner", brand.advertisementFile);
     }
 
     try {
       setIsSubmitting(true);
       await USER_URL.put(`/products/admin/brands/${brandId}/`, formData, {
         headers: {
           "Content-Type": "multipart/form-data", // Ensure the correct content type
         },
       });
       toast.success("Brand updated successfully!");
     } catch (error) {
       console.error("Error updating brand:", error.response || error.message);
       toast.error(error.response?.data?.message || "Failed to update brand.");
     } finally {
       setIsSubmitting(false);
     }
   };
 
   if (isLoading) return <div>Loading...</div>;
 
   return (
     <div className="p-3">
     <div className="border-2 rounded-lg shadow-lg bg-gray-50 p-6">
       <h2 className="text-green-700 text-2xl sm:text-3xl font-bold text-center mb-6">
         Edit Brand
       </h2>
   
       <form onSubmit={handleSubmit} className="space-y-6">
         {/* Brand Name and Logo in One Line for Large Screens, Stacked on Mobile */}
         <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
           <div className="">
             <label className="block text-gray-700 font-medium mb-2">Brand Name</label>
             <input
               type="text"
               name="brandName"
               value={brand.brandName}
               onChange={handleInputChange}
               placeholder="Enter brand name"
               className="w-full border-gray-300 border p-3 rounded focus:outline-none focus:ring focus:ring-green-200"
             />
           </div>
   
           <div className="grid justify-end">
             <label className="block text-gray-700 font-medium mb-2">Logo</label>
             <div className="flex items-center space-x-4">
               <div className="w-24 h-24 border-2 rounded-lg flex items-center justify-center overflow-hidden bg-gray-100">
                 {brand.logo ? (
                   <img
                     src={brand.logo.startsWith("blob:") ? brand.logo : `${BASE_URL}${brand.logo}`}
                     alt={brand.brandName}
                     className="h-full w-full object-cover"
                   />
                 ) : (
                   <span className="text-gray-500 text-sm">No Logo</span>
                 )}
               </div>
               <input
                 type="file"
                 accept="image/*"
                 onChange={(e) => handleFileChange(e, "logo")}
                 className="hidden"
                 id="logoUpload"
               />
               <label
                 htmlFor="logoUpload"
                 className="bg-green-600 text-white px-4 py-2 text-sm sm:text-base rounded cursor-pointer hover:bg-green-700"
               >
                 Upload Logo
               </label>
             </div>
           </div>
         </div>
   
         {/* Banner Image Upload in One Line */}
         <div>
           <label className="block text-gray-700 font-medium mb-2">Banner Image</label>
           <div className="flex flex-col sm:flex-row items-center gap-6">
             {brand.brand_banner ? (
               <div className="relative w-full">
                 <img
                   src={brand.brand_banner.startsWith("blob:") ? brand.brand_banner : `${BASE_URL}${brand.brand_banner}`}
                   alt="Banner Preview"
                   className="w-full h-48 sm:h-56 object-cover rounded-md mb-4"
                 />
                 {/* <button
                   type="button"
                   className="absolute top-2 right-2 bg-red-500 text-white rounded-full px-2 py-1 text-xs"
                   onClick={() => setBrand((prev) => ({ ...prev, brand_banner: "", advertisementFile: null }))}
                 >
                   Remove
                 </button> */}
               </div>
             ) : (
               <div className="w-full h-48 sm:h-56 bg-gray-100 flex items-center justify-center rounded-md text-gray-500">
                 No Banner Selected
               </div>
             )}
   
             <input
               type="file"
               accept="image/*"
               onChange={(e) => handleFileChange(e, "advertisement")}
               className="hidden"
               id="advertisementUpload"
             />
           </div>
         </div>
   
         {/* Buttons Section */}
         <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
           {/* Add Banner Button (Left-Aligned on Desktop, Centered on Mobile) */}
           <div className=" text-center sm:text-left">
             <label
               htmlFor="advertisementUpload"
               className="border-2 w-full sm:w-[250px] border-green-600 text-green-600 text-sm sm:text-base py-2 px-16 rounded cursor-pointer hover:bg-green-600 hover:text-white transition"
             >
               Add Banner
             </label>
           </div>
   
           {/* Confirm Update Button (Right-Aligned on Desktop, Centered on Mobile) */}
           <div className=" text-center sm:text-right">
             <button
               type="submit"
               disabled={isSubmitting}
               className={`w-full sm:w-[250px] bg-green-600 text-white text-sm sm:text-base py-2 px-12 rounded transition ${isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:bg-green-700"}`}
             >
               {isSubmitting ? "Submitting..." : "Confirm Update"}
             </button>
           </div>
         </div>
       </form>
   
       {/* Back Button */}
       <div className="mt-4 text-center">
         <button
           onClick={() => navigate(-1)}
           className="text-green-600 hover:text-green-700 font-medium text-sm"
         >
           &lt; Back to Brand List
         </button>
       </div>
     </div>
     <ToastContainer position="top-center" autoClose={2000} />
   </div>
   
   );
 }

export default AdminEditBrand